import { motion } from 'framer-motion';
import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import type { BlogPost } from '../../services/api';

import styles from './FullscreenBlogPost.module.css';

/**
 * FullscreenBlogPost Props
 */
type FullscreenBlogPostProps = {
  readonly post: BlogPost;
  readonly isOpen: boolean;
  readonly onClose: () => void;
  readonly onNext?: () => void;
  readonly onPrevious?: () => void;
};

/**
 * FullscreenBlogPost Component
 *
 * Displays a single blog post in fullscreen modal/overlay.
 * Features:
 * - Fullscreen modal with dark backdrop
 * - Smooth entry/exit animations
 * - Navigation controls (next/previous)
 * - Close button and ESC key support
 * - Keyboard navigation (arrow keys, ESC)
 * - Scrollable content area for long posts
 * - Renders sanitized HTML content
 */
const FullscreenBlogPost: React.FC<FullscreenBlogPostProps> = ({
  post,
  isOpen,
  onClose,
  onNext,
  onPrevious,
}) => {
  /**
   * Handle keyboard events
   */
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'Escape':
          onClose();
          break;
        case 'ArrowRight':
          if (onNext) {
            event.preventDefault();
            onNext();
          }
          break;
        case 'ArrowLeft':
          if (onPrevious) {
            event.preventDefault();
            onPrevious();
          }
          break;
      }
    },
    [isOpen, onClose, onNext, onPrevious]
  );

  /**
   * Set up keyboard event listener
   */
  useEffect(() => {
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when fullscreen is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, handleKeyDown]);

  /**
   * Handle backdrop click to close
   */
  const handleBackdropClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (event.target === event.currentTarget) {
        onClose();
      }
    },
    [onClose]
  );

  /**
   * Format date for display
   */
  const formatDate = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!isOpen) return null;

  return createPortal(
    <motion.div
      className={styles.fullscreen}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      onClick={handleBackdropClick}
    >
      {/* Backdrop - Click to close */}
      <div className={styles.backdrop} onClick={onClose} />

      {/* Close Button */}
      <button className={styles.closeButton} onClick={onClose} aria-label="Close post">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M6 18L18 6M6 6l12 12"
          />
        </svg>
      </button>

      {/* Content Container */}
      <motion.div
        className={styles.content}
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        transition={{ duration: 0.3, ease: 'easeOut' }}
      >
        {/* Featured Image */}
        {post.featuredImage?.url && (
          <div className={styles.featuredImage}>
            <img src={post.featuredImage.url} alt={post.featuredImage.alt ?? post.title} />
          </div>
        )}

        {/* Post Header */}
        <header className={styles.postHeader}>
          <h1 className={styles.title}>{post.title}</h1>
          {post.publishedAt && (
            <time className={styles.date}>{formatDate(post.publishedAt)}</time>
          )}
        </header>

        {/* Post Content */}
        <article
          className={styles.postContent}
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      </motion.div>

      {/* Navigation Controls */}
      {onPrevious ? (
        <button
          className={`${styles.navButton} ${styles.prevButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onPrevious();
          }}
          aria-label="Previous post"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
        </button>
      ) : null}

      {onNext ? (
        <button
          className={`${styles.navButton} ${styles.nextButton}`}
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          aria-label="Next post"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      ) : null}

      {/* Keyboard Hint */}
      <div className={styles.keyboardHint}>
        <span>ESC or click background to close</span>
        {(onNext ?? onPrevious) ? <span> &bull; Arrow keys to navigate</span> : null}
      </div>
    </motion.div>,
    document.body
  );
};

export default FullscreenBlogPost;
